import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useIncidents,
  getSeverityLabel,
  getSeverityColor,
  getIncidentStatusLabel,
  getIncidentStatusColor,
} from '@/hooks/useRPL';
import { IncidentFormDialog } from './IncidentFormDialog';
import type { IncidentStatus, IncidentSeverity, IncidentDetailed } from '@/types/rpl';
import { Search, Plus, AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface IncidentListProps {
  onSelectIncident?: (incident: IncidentDetailed) => void;
}

export function IncidentList({ onSelectIncident }: IncidentListProps) {
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<IncidentSeverity | 'all'>('all');
  const { data: incidents, isLoading } = useIncidents({
    status: statusFilter,
    severity: severityFilter,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<IncidentDetailed | null>(null);

  const filteredIncidents = incidents?.filter((inc) => {
    const search = searchTerm.toLowerCase();
    return (
      inc.incident_number.toLowerCase().includes(search) ||
      inc.title.toLowerCase().includes(search) ||
      inc.reporter_name?.toLowerCase().includes(search) ||
      inc.victim_name?.toLowerCase().includes(search)
    );
  });

  const handleEdit = (incident: IncidentDetailed) => {
    setSelectedIncident(incident);
    setFormDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedIncident(null);
    setFormDialogOpen(true);
  };

  const getStatusIcon = (status: IncidentStatus) => {
    switch (status) {
      case 'open':
        return <AlertTriangle className="w-4 h-4" />;
      case 'investigating':
        return <Clock className="w-4 h-4" />;
      case 'closed':
        return <CheckCircle className="w-4 h-4" />;
      case 'dismissed':
        return <XCircle className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar incidentes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={handleNew} className="bg-gradient-magma">
          <Plus className="w-4 h-4 mr-2" />
          Reportar Incidente
        </Button>
      </div>

      <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as IncidentStatus | 'all')}>
        <TabsList>
          <TabsTrigger value="all">Todos</TabsTrigger>
          <TabsTrigger value="open">Abiertos</TabsTrigger>
          <TabsTrigger value="investigating">En Investigacion</TabsTrigger>
          <TabsTrigger value="closed">Cerrados</TabsTrigger>
        </TabsList>
      </Tabs>

      {filteredIncidents?.length === 0 ? (
        <Card className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No hay incidentes registrados</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredIncidents?.map((incident) => (
            <Card
              key={incident.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onSelectIncident?.(incident) || handleEdit(incident)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm text-muted-foreground">
                        {incident.incident_number}
                      </span>
                      <Badge className={getSeverityColor(incident.severity)}>
                        {getSeverityLabel(incident.severity)}
                      </Badge>
                      <Badge variant="outline" className={getIncidentStatusColor(incident.status)}>
                        {getStatusIcon(incident.status)}
                        <span className="ml-1">{getIncidentStatusLabel(incident.status)}</span>
                      </Badge>
                    </div>
                    <h3 className="font-semibold truncate">{incident.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-1">{incident.description}</p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground whitespace-nowrap">
                    <p>{format(new Date(incident.occurred_at), 'dd/MM/yyyy HH:mm', { locale: es })}</p>
                    {incident.victim_name && <p className="text-xs">Afectado: {incident.victim_name}</p>}
                    {incident.days_lost > 0 && (
                      <p className="text-xs text-red-500">{incident.days_lost} dias de baja</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <IncidentFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        incident={selectedIncident}
      />
    </div>
  );
}
